import {
  useGetTriviaTemplatesQuery,
  useGetTriviaTemplateQuery,
  useGetTriviaStatsQuery,
  useGetTriviaQuestionsQuery,
  useGenerateTriviaQuestionsMutation,
  useDeactivateTriviaQuestionsMutation,
  useActivateTriviaQuestionsMutation,
  useDeleteTriviaQuestionsMutation,
  useGetTriviaGameQuestionsQuery,
  useSaveQuestionsMutation,
} from "../store/api/mlbApi";

export const useTriviaTemplates = () => {
  const { data, error, isLoading, refetch } = useGetTriviaTemplatesQuery();

  // Debug what we're receiving
  console.log("Frontend received data:", data);
  console.log("Frontend received error:", error);
  console.log("Data type:", typeof data);
  console.log("Data is array:", Array.isArray(data));

  // Handle response structure - now data is directly the templates array
  let templates = [];
  if (data) {
    if (Array.isArray(data)) {
      templates = data;
      console.log("Using data as templates array, length:", templates.length);
    } else if (Array.isArray(data.templates)) {
      templates = data.templates;
      console.log("Using data.templates, length:", templates.length);
    } else {
      console.log("Data is not an array and doesn't have templates array");
    }
  }

  console.log("Final templates:", templates);

  return {
    templates,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

export const useTriviaTemplate = (templateId) => {
  const { data, error, isLoading, refetch } = useGetTriviaTemplateQuery(
    templateId,
    {
      skip: !templateId,
    }
  );

  return {
    template: data?.template || null,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

export const useTriviaStats = () => {
  const { data, error, isLoading, refetch } = useGetTriviaStatsQuery();

  return {
    stats: data?.stats || null,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

export const useTriviaQuestions = (paginationParams = {}) => {
  const { data, error, isLoading, refetch } =
    useGetTriviaQuestionsQuery(paginationParams);

  return {
    questions: data?.questions || [],
    totalResults: data?.totalResults || 0,
    currentPage: data?.currentPage || 1,
    pageSize: data?.pageSize || 25,
    totalPages: data?.totalPages || 0,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

export const useGenerateQuestions = () => {
  const [generateQuestions, { isLoading, error }] =
    useGenerateTriviaQuestionsMutation();

  const generateTriviaQuestions = async (params) => {
    try {
      const result = await generateQuestions(params).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error:
          err?.data?.error || err?.message || "Failed to generate questions",
      };
    }
  };

  return {
    generateTriviaQuestions,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

export const useDeactivateQuestions = () => {
  const [deactivateQuestions, { isLoading, error }] =
    useDeactivateTriviaQuestionsMutation();

  const deactivateTriviaQuestions = async (questionIds) => {
    try {
      const result = await deactivateQuestions(questionIds).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error:
          err?.data?.error || err?.message || "Failed to deactivate questions",
      };
    }
  };

  return {
    deactivateTriviaQuestions,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

export const useActivateQuestions = () => {
  const [activateQuestions, { isLoading, error }] =
    useActivateTriviaQuestionsMutation();

  const activateTriviaQuestions = async (questionIds) => {
    try {
      const result = await activateQuestions(questionIds).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error:
          err?.data?.error || err?.message || "Failed to activate questions",
      };
    }
  };

  return {
    activateTriviaQuestions,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

export const useDeleteQuestions = () => {
  const [deleteQuestions, { isLoading, error }] =
    useDeleteTriviaQuestionsMutation();

  const deleteTriviaQuestions = async (questionIds) => {
    try {
      const result = await deleteQuestions(questionIds).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to delete questions",
      };
    }
  };

  return {
    deleteTriviaQuestions,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

export const useTriviaGameQuestions = (params = {}) => {
  const { data, error, isLoading, refetch } =
    useGetTriviaGameQuestionsQuery(params);

  return {
    questions: data?.questions || [],
    count: data?.count || 0,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

export const useSaveQuestions = () => {
  const [saveQuestions, { isLoading, error }] = useSaveQuestionsMutation();

  const saveTriviaQuestions = async (questions) => {
    try {
      const result = await saveQuestions(questions).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to save questions",
      };
    }
  };

  return {
    saveTriviaQuestions,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};
